import { ToastSuccess, ToastError } from 'components/Toaster';
import { CertificacoesService } from 'core/http/service/Certificacoes.service';
import { ProfileService } from 'core/http/service/Profile.service';
import { UsuariosCertificadosService } from 'core/http/service/UsuariosCertificados.service';
import { useAppSelector } from 'hooks/LocalReduxThunk';
import { CertificacoesModel } from 'models/certificacoes/CertificacoesModel';
import UsuarioDTO from 'models/UsuarioDTO';
import React, { useEffect, useMemo, useState } from 'react';

//services
const servicesUsuariosCertificados = new UsuariosCertificadosService();
const servicesCertificacoes = new CertificacoesService();
const perfilUsuarioService = new ProfileService();

interface ICertificacao {
    codigo: number;
    nome: string;
    textoSolicitacao: string;
    precisaSolicitar: boolean;
    certificateTemplateInternal: boolean;
    link: string;
}

export interface ICertificadoPublic {
    codigo: number | null;
    nome: string;
    cpf: string;
    email: string;
    estado: string;
    municipio: string;
    certificacao: ICertificacao;
    emited: boolean;
    dataCreate: string;
}

export const CertificadoPublicHook = () => {
    const [certificados, setCertificados] = useState<ICertificadoPublic[]>([]);
    const [page, setPage] = useState<number>(0);
    const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
    const [loadingSendEmail, setLoadingSendEmail] = useState<boolean>(false);

    const [userAtualizouNome, setUserAtualizouNome] = useState<boolean>(false);
    const [nomeCompleto, setNomeCompleto] = useState<string>('');
    const [openModalAtualizarNome, setOpenModalAtualizarNome] = useState<boolean>(false);

    const [loadingCertificado, setLoadingCertificado] = useState<boolean>(false);

    const usuarioLogado: UsuarioDTO = useAppSelector(state => state.authenticationReducer.usuarioLogado);

    // Função para fazer download de arquivo
    const downloadFile = (data: any, fileName: string) => {
        const file = new Blob([data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(fileURL);
    };

    //função para buscar os certificados do usuário
    const fetcherCertificados = async () => {
        try {
            const { data, status } = await servicesUsuariosCertificados.allByUser();

            if (status === 200) {
                setCertificados(data);
            }
        } catch (error) {
            console.log('Erro ao buscar certificados:', error);
        }
    };

    const handleClickCertificado = async (CertificacoesModel: ICertificacao, emited?: boolean, index?: number) => {
        setLoadingIndex(index ?? null);
        try {
            if (emited && !CertificacoesModel.certificateTemplateInternal) {
                window.open(CertificacoesModel.link?.toString());
                return;
            }

            if (!userAtualizouNome) {
                setOpenModalAtualizarNome(true);
                setLoadingIndex(null);
                setNomeCompleto(usuarioLogado.primeiroNome + ' ' + usuarioLogado.ultimoNome);
            }

            if (CertificacoesModel.certificateTemplateInternal) {
                const response = await servicesCertificacoes.handleGenerateCertificado(CertificacoesModel.codigo ?? -1);
                if (response && response.status === 200) {
                    downloadFile(response.data, `Certificado_${CertificacoesModel.nome}_${new Date().getTime()}.pdf`);
                    ToastSuccess('Certificado baixado com sucesso!');
                    await fetcherCertificados();
                    return;
                }

                ToastError('Não foi possível gerar o certificado no momento. Tente novamente mais tarde.');
                return;
            }

            if (CertificacoesModel.precisaSolicitar) {
                const response = await servicesUsuariosCertificados.associarUser(CertificacoesModel.codigo ?? -1, false);

                if (response && response.status === 201) {
                    ToastSuccess('Certificado solicitado com sucesso!');
                    await fetcherCertificados();
                } else {
                    ToastSuccess('Solicitação de certificado realizada com sucesso!');
                }

                return;
            }

            if (CertificacoesModel.link) {
                const response = await servicesUsuariosCertificados.associarUser(CertificacoesModel.codigo ?? -1, true);

                if (response && (response.status === 201 || response.status === 200)) {
                    window.open(CertificacoesModel.link?.toString());
                    ToastSuccess('Certificado emitido com sucesso!');
                    await fetcherCertificados();
                } else {
                    ToastSuccess('Solicitação de certificado realizada com sucesso!');
                }

                return;
            }
            ToastError('Não foi possível processar o certificado. Tente novamente.');
        } catch (error) {
            ToastError('Erro ao processar certificado. Tente novamente.');
        } finally {
            setLoadingIndex(null);
        }
    };

    const handleSendEmailCertificado = async (certificadoCodigo: number, email: string) => {
        try {
            setLoadingSendEmail(true);
            await servicesCertificacoes.sendEmailCertificado(certificadoCodigo, email);
        } catch (error) {
            ToastError('Erro ao enviar e-mail. Tente novamente.');
        } finally {
            setLoadingSendEmail(false);
            ToastSuccess('E-mail enviado com sucesso!');
            fetcherCertificados();
        }
    };

    const handleAtualizarNomeUsuario = async (nome: string) => {
        try {
            setLoadingCertificado(true);
            const { status } = await perfilUsuarioService.atualizarNomeCompletoUsuario(nome);

            if (status == 200) {
                setUserAtualizouNome(true);
                setOpenModalAtualizarNome(false);
                fetcherCertificados();
            }
        } catch (error) {
            console.log('🚀 ~ handleAtualizarNomeUsuario ~ error:', error);
        } finally {
            setLoadingCertificado(false);
        }
    };

    useEffect(() => {
        const getUserAtualizouONome = async () => {
            const response = await perfilUsuarioService.userAtualizouONome();

            setUserAtualizouNome(response.data);
        };

        getUserAtualizouONome();
    }, []);

    const returnRoute = () => {
        setPage(0);
    };

    useEffect(() => {
        fetcherCertificados();
    }, []);

    return {
        certificados,
        page,
        loadingIndex,
        loadingSendEmail,
        setPage,
        setLoadingIndex,
        handleClickCertificado,
        returnRoute,
        handleSendEmailCertificado,
        handleAtualizarNomeUsuario,
        openModalAtualizarNome,
        setOpenModalAtualizarNome,
        nomeCompleto,
        loadingCertificado,
    };
};
